<template>
  <div class="sales-by-team-page bg-white shadow-sm border border-slate-200 overflow-hidden">
    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Sales By TIN</span>
      </div>
    </div>

    <form @submit.prevent="onSave">
      <div class="px-6 pb-3 pt-2">
        <div class="flex items-center gap-3 min-w-[220px] max-w-sm">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0">Select Date:</label>
          <input
            v-model="selectedDate"
            type="date"
            class="flex-1 min-w-[9rem] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            @change="loadRowsForDate"
          >
        </div>
      </div>

      <div class="px-6 pt-2 pb-6">
        <div class="text-[0.78rem] font-semibold capitalize tracking-wide text-center text-[#1e293b] bg-white rounded-t-md py-1.5 border-b border-slate-200">
          Sales By TIN
        </div>

        <div class="relative overflow-auto custom-scrollbar border border-slate-200 rounded-lg shadow-sm bg-white">
          <table class="w-full today-cash-table min-w-max">
            <thead>
              <tr>
                <th class="col-sn">No.</th>
                <th>Buyer TIN</th>
                <th>MRC</th>
                <th>Reciept No/FS No</th>
                <th>Reciept Date</th>
                <th class="num">Quantity</th>
                <th class="num">Price Befor Tax</th>
                <th>COM_CODE</th>
                <th>COM_DETAIL</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in rows"
                :key="row.id"
                class="hover:bg-slate-50 transition-colors border-b"
              >
                <td class="col-sn">
                  <div v-if="index > 0" class="col-idx-actions">
                    <span class="col-idx-num">{{ index + 1 }}</span>
                    <button
                      type="button"
                      class="remove-row-btn"
                      title="Remove row"
                      aria-label="Remove row"
                      @click="removeRow(index)"
                    >
                      ×
                    </button>
                  </div>
                  <template v-else>{{ index + 1 }}</template>
                </td>
                <td>
                  <input v-model.trim="row.buyerTin" type="text" class="income-table-field" />
                </td>
                <td>
                  <input v-model.trim="row.mrc" type="text" class="income-table-field" />
                </td>
                <td>
                  <input v-model.trim="row.receiptNoFsNo" type="text" class="income-table-field" />
                </td>
                <td>
                  <input v-model="row.receiptDate" type="date" class="income-table-field cursor-pointer" />
                </td>
                <td class="num">
                  <input v-model.number="row.quantity" type="number" min="0" step="0.01" class="income-table-field" />
                </td>
                <td class="num">
                  <input v-model.number="row.priceBeforeTax" type="number" min="0" step="0.01" class="income-table-field" />
                </td>
                <td>
                  <input v-model.trim="row.comCode" type="text" class="income-table-field" />
                </td>
                <td>
                  <input v-model.trim="row.comDetail" type="text" class="income-table-field" />
                </td>
                <td>
                  <input v-model.trim="row.description" type="text" class="income-table-field" />
                </td>
              </tr>
              <tr class="add-row-tr border-b">
                <td class="col-sn">
                  <button
                    type="button"
                    class="add-row-btn"
                    title="Add row"
                    aria-label="Add row"
                    @click="addRow"
                  >
                    +
                  </button>
                </td>
                <td colspan="9"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-center pt-6">
          <button
            type="submit"
            :disabled="isSaving"
            class="min-w-[8rem] px-6 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSaving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const toast = reactive({ show: false, message: '', variant: 'success' })
let toastTimer = null

function showToast(message, variant = 'success', durationMs = 2200) {
  toast.message = message
  toast.variant = variant
  toast.show = true
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.show = false
  }, durationMs)
}

function createRow(data = {}) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    buyerTin: data.buyerTin || '',
    mrc: data.mrc || '',
    receiptNoFsNo: data.receiptNoFsNo || '',
    receiptDate: data.receiptDate || '',
    quantity: data.quantity ?? null,
    priceBeforeTax: data.priceBeforeTax ?? null,
    comCode: data.comCode || '',
    comDetail: data.comDetail || '',
    description: data.description || ''
  }
}

const selectedDate = ref('')
const rows = ref([createRow()])
const isSaving = ref(false)
const isLoading = ref(false)

function addRow() {
  rows.value.push(createRow())
}

function removeRow(index) {
  if (index <= 0) return
  rows.value.splice(index, 1)
}

function serializeRow(row) {
  return {
    buyerTin: String(row.buyerTin || '').trim(),
    mrc: String(row.mrc || '').trim(),
    receiptNoFsNo: String(row.receiptNoFsNo || '').trim(),
    receiptDate: String(row.receiptDate || '').trim(),
    quantity: row.quantity == null || row.quantity === '' ? 0 : Number(row.quantity),
    priceBeforeTax: row.priceBeforeTax == null || row.priceBeforeTax === '' ? 0 : Number(row.priceBeforeTax),
    comCode: String(row.comCode || '').trim(),
    comDetail: String(row.comDetail || '').trim(),
    description: String(row.description || '').trim()
  }
}

async function loadRowsForDate() {
  const postedDate = String(selectedDate.value || '').trim()
  if (!postedDate) {
    rows.value = [createRow()]
    return
  }

  isLoading.value = true
  try {
    const qs = new URLSearchParams({ postedDate })
    const res = await apiFetch(`/finance/sales-by-team?${qs.toString()}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load Sales By TIN records.')
    }

    const data = await res.json().catch(() => [])
    if (!Array.isArray(data) || data.length === 0) {
      rows.value = [createRow()]
      return
    }

    rows.value = data.map((row) => createRow({
      buyerTin: row.buyerTin,
      mrc: row.mrc,
      receiptNoFsNo: row.receiptNoFsNo,
      receiptDate: row.receiptDate,
      quantity: row.quantity,
      priceBeforeTax: row.priceBeforeTax,
      comCode: row.comCode,
      comDetail: row.comDetail,
      description: row.description
    }))
  } catch (error) {
    rows.value = [createRow()]
    showToast(error.message || 'Failed to load Sales By TIN records.', 'error')
  } finally {
    isLoading.value = false
  }
}

function resetFormToDefault() {
  selectedDate.value = ''
  rows.value = [createRow()]
}

async function onSave() {
  if (isSaving.value || isLoading.value) return

  const postedDate = String(selectedDate.value || '').trim()
  if (!postedDate) {
    showToast('Please select a date.', 'error')
    return
  }

  isSaving.value = true
  try {
    const payload = {
      postedDate,
      rows: rows.value.map(serializeRow)
    }

    const res = await apiFetch('/finance/sales-by-team/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to save Sales By TIN.')
    }

    showToast('Sales By TIN saved successfully.', 'success')
    resetFormToDefault()
  } catch (error) {
    showToast(error.message || 'Failed to save Sales By TIN.', 'error')
  } finally {
    isSaving.value = false
  }
}

</script>

<style scoped>
.today-cash-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
  width: auto;
  min-width: 100%;
}

.today-cash-table th,
.today-cash-table td {
  padding: 4px 10px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
}

.today-cash-table th {
  background: #f1f5f9;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.col-sn {
  width: 2.75rem;
  min-width: 2.75rem;
  text-align: center;
}

.today-cash-table th.num,
.today-cash-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
  white-space: nowrap;
}

.today-cash-table input.income-table-field {
  box-sizing: border-box;
  width: 100%;
  min-width: 7.5rem;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 3px;
  outline: none;
  background-color: #fef2f2;
  font-size: 0.72rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
  transition: all 0.15s;
}

.today-cash-table input.income-table-field:focus {
  border-color: #0d4169;
  background-color: #fff;
}

.col-idx-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
  max-width: 100%;
}

.col-idx-num {
  min-width: 0.65rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.remove-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  padding: 0;
  border: 1px solid #fecaca;
  border-radius: 0.25rem;
  background: #fff;
  color: #b91c1c;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.remove-row-btn:hover {
  background: #fef2f2;
  border-color: #f87171;
}

.add-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  background: #fff;
  color: #0d4169;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.add-row-btn:hover {
  background: #f1f5f9;
  border-color: #0d4169;
}
</style>
